import axios from 'axios';
import Lottie from 'lottie-react';
import { ComponentPropsWithoutRef, memo, useEffect, useState } from 'react';

import handleAxiosError from 'lib/errors/handleAxiosError';
import useToggle from 'lib/hooks/useToggle';

type IMGWithoutSrc = Omit<ComponentPropsWithoutRef<'img'>, 'src'>;

export type ImageProps = IMGWithoutSrc & {
  className?: string;
  contentType?: string | null;
  'data-test'?: string;
  environment?: string;

  // src was occasionally not set in production
  src?: string;
  onLoad?: () => void;
  onError?: () => void;
};

/**
 * NOTE: we do not evict this cache. While this does run the risk of memory
 * leak, it's bounded by the number of images in an ITV, so shouldn't be a
 * problem.
 *
 * @private we only import this for specs.
 */
export const assetCache: Record<string, string | Record<string, unknown>> = {};

const Image = memo(function ImageInner({
  contentType,
  src,
  'data-test': dataTest = 'image',
  environment,
  ...props
}: ImageProps) {
  const [loading, { disable: loadEnd }] = useToggle(true);

  let pathname: string;
  try {
    // Absolute path -- remove query string
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pathname = new URL(src).pathname;
  } catch {
    // Relative paths cannot contain query strings
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pathname = src;
  }

  const [assetData, setAssetData] = useState<string | Record<string, unknown>>(
    assetCache[pathname],
  );

  const normalizedPathname = pathname?.toLowerCase();

  const isImage =
    normalizedPathname?.endsWith('.png') ||
    normalizedPathname?.endsWith('.gif') ||
    normalizedPathname?.endsWith('.jpg') ||
    normalizedPathname?.endsWith('.jpeg') ||
    src?.startsWith('data:image/png;') ||
    src?.startsWith('data:image/jpeg;') ||
    src?.startsWith('blob:') ||
    contentType === 'image/png' ||
    contentType === 'image/gif' ||
    contentType === 'image/jpg' ||
    contentType === 'image/jpeg';

  const isSvg =
    contentType === 'image/svg+xml' ||
    normalizedPathname?.endsWith('.svg') ||
    src?.startsWith('data:image/svg+xml');

  const isJson = normalizedPathname?.endsWith('.json') || contentType === 'application/json';

  useEffect(() => {
    if (
      (environment ? environment === 'production' : import.meta.env.PROD) &&
      !(
        src?.startsWith('https://files.withpersona.com/') ||
        src?.startsWith('https://files.withpersona-staging.com/')
      )
    ) {
      loadEnd();
      // Only allow inline loading of Persona-hosted SVGs
      return;
    }

    if (isSvg) {
      // We fetch the SVG then use it as the src for an <img> rather than inlining to avoid XSS. We can't pass src
      // directly to the <img> because Rails-uploaded SVGs are reported as application/octet-stream.
      // https://github.com/rails/rails/issues/34665
      // These user-uploaded SVGs don't need to be themed or otherwise styled.
      axios
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .get(src)
        // Default encoding results in the file being unreadable.
        .then(({ data }) => {
          const dataUri = `data:image/svg+xml;base64,${btoa(data)}`;
          assetCache[pathname] = dataUri;
          setAssetData(dataUri);
        })
        .catch((error) =>
          handleAxiosError({
            error,
            method: 'GET',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            uri: src,
          }),
        )
        .finally(loadEnd);
    } else if (isJson) {
      // For JSON and Lottie, we have to call the onLoad and onError handlers here since Lottie
      // does not fire events.
      axios
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .get(src)
        .then(({ data }) => {
          assetCache[pathname] = data;
          setAssetData(data);
          props.onLoad?.();
        })
        .catch((error) => {
          props.onError?.();
          handleAxiosError({
            error,
            method: 'GET',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            uri: src,
          });
        })
        .finally(loadEnd);
    }
  }, [src, isImage, isJson, isSvg]);

  if (!src) {
    return null;
  }

  if (isImage) {
    return <img {...props} src={src} data-test={dataTest} />;
  } else if (isSvg && assetData != null) {
    return <img {...props} src={assetData as string} data-test={dataTest} />;
  } else if (isJson && assetData != null) {
    return (
      <Lottie
        role='img'
        animationData={assetData as Record<string, unknown>}
        style={{ height: props.height ?? 'auto', width: props.width ?? 'auto' }}
        // TODO: Lottie doesn't fire events
        // onDataReady={props.onLoad}
        // onDataFailed={props.onError}
      />
    );
  } else {
    return (
      <div
        aria-label={loading ? 'loading image' : 'no image'}
        data-test={`${dataTest}__${loading ? 'loading' : 'empty'}`}
        className={props.className}
      />
    );
  }
});

export default Image;
